import React from "react";

export const PageHeader = ({ overline, title, subtitle, actions }) => (
  <div className="border-b border-zinc-800 px-8 py-6 flex items-end justify-between gap-6 flex-wrap">
    <div>
      {overline && <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2 font-mono">{overline}</div>}
      <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-zinc-400 mt-1 max-w-2xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2">{actions}</div>}
  </div>
);

export const Surface = ({ className = "", children, ...rest }) => (
  <div className={`bg-[#141414] border border-zinc-800 ${className}`} {...rest}>
    {children}
  </div>
);

export const StatTile = ({ label, value, sub, accent = "zinc", testId }) => (
  <Surface className="p-5" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-2">{label}</div>
    <div className={`text-2xl font-mono tracking-tight ${accent === "amber" ? "text-amber" : accent === "green" ? "text-green-400" : accent === "red" ? "text-red-400" : "text-zinc-100"}`}>
      {value}
    </div>
    {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
  </Surface>
);

export const Btn = ({ variant = "primary", className = "", ...props }) => {
  const base = "h-10 px-4 text-xs uppercase tracking-[0.15em] font-medium border transition-colors duration-150";
  const styles = variant === "primary"
    ? "bg-amber text-black border-amber hover:bg-[#FFC033]"
    : variant === "danger"
    ? "bg-transparent text-red-400 border-red-500/50 hover:bg-red-500/10"
    : "bg-transparent text-zinc-200 border-zinc-700 hover:border-amber hover:text-amber";
  return <button className={`${base} ${styles} ${className}`} {...props} />;
};

export const Field = ({ label, children, hint }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-mono">{label}</div>
    {children}
    {hint && <div className="text-[10px] text-zinc-600 mt-1">{hint}</div>}
  </label>
);

export const Input = (props) => (
  <input
    {...props}
    className={`w-full h-10 bg-[#0A0A0A] border border-zinc-800 px-3 text-sm text-zinc-100 focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber font-mono ${props.className || ""}`}
  />
);

export const Select = ({ children, ...props }) => (
  <select
    {...props}
    className={`w-full h-10 bg-[#0A0A0A] border border-zinc-800 px-3 text-sm text-zinc-100 focus:outline-none focus:border-amber font-mono ${props.className || ""}`}
  >
    {children}
  </select>
);

export const Textarea = (props) => (
  <textarea
    {...props}
    className={`w-full bg-[#0A0A0A] border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-amber ${props.className || ""}`}
  />
);

/**
 * CurrencyInput — drop-in replacement for <Input type="number" step="0.01" />
 * for any monetary field. Behaviour:
 *   • Visible value is always quantised to ≤ 2 decimals (string editing,
 *     so the cursor doesn't jump while the user types).
 *   • On change, fires `onChange(numericValue)` with a Number quantised to 2dp
 *     using commercial round-half-up. Empty input → 0.
 *   • On blur, normalises the input string to exactly 2 decimals (e.g. "12" → "12.00").
 *   • Strictly limits typing to 2 decimal places via input pattern.
 */
const round2Local = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.round((v + Number.EPSILON) * 100) / 100;
};

export const CurrencyInput = React.forwardRef(function CurrencyInput(
  { value, onChange, disabled, placeholder, "data-testid": testId, className = "", min, max, ...rest },
  ref
) {
  const [text, setText] = React.useState(() =>
    value === undefined || value === null || value === "" ? "" : String(round2Local(value))
  );
  // When the parent's value updates externally (e.g. after fetch), sync display.
  React.useEffect(() => {
    if (value === undefined || value === null || value === "") {
      setText("");
      return;
    }
    const num = round2Local(value);
    // Don't fight the user mid-edit if they typed the same numeric value.
    if (Number(text) !== num) setText(String(num));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value;
    // Allow only digits, one optional minus, optional dot, up to 2 fractional digits
    if (raw === "" || /^-?\d*\.?\d{0,2}$/.test(raw)) {
      setText(raw);
      const num = raw === "" || raw === "-" || raw === "." ? 0 : Number(raw);
      onChange?.(round2Local(num));
    }
  };

  const handleBlur = () => {
    if (text === "" || text === "-") {
      setText("");
      onChange?.(0);
      return;
    }
    const n = round2Local(text);
    setText(n.toFixed(2));
    onChange?.(n);
  };

  return (
    <input
      ref={ref}
      type="text"
      inputMode="decimal"
      value={text}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder={placeholder}
      data-testid={testId}
      min={min}
      max={max}
      className={`w-full h-10 bg-[#0A0A0A] border border-zinc-800 px-3 text-sm text-zinc-100 font-mono focus:outline-none focus:border-amber disabled:opacity-50 ${className}`}
      {...rest}
    />
  );
});

export const StatusTag = ({ status }) => {
  const map = {
    available: "tag-green",
    active: "tag-green",
    confirmed: "tag-green",
    completed: "tag-green",
    maintenance: "tag-amber",
    pending: "tag-amber",
    training_only: "tag-amber",
    sent: "tag-blue",
    scheduled: "tag-blue",
    grounded: "tag-red",
    suspended: "tag-red",
    cancelled: "tag-red",
    minor: "tag-zinc",
    limitation: "tag-amber",
    grounding: "tag-red",
    flight: "tag-blue",
    training: "tag-amber",
  };
  return <span className={`tag ${map[status] || "tag-zinc"}`}>{status?.replace("_", " ")}</span>;
};
